import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { AnimatePresence, motion } from 'framer-motion';
import { toast } from 'react-toastify';
import {
  Plus,
  Edit2,
  Trash2,
  Search,
  X,
  Smartphone,
  ExternalLink
} from 'lucide-react';
import ImageDropzone from '../components/ImageDropzone';
import Logo from '../../assets/quivix.png';

const emptyForm = {
  title: '',
  description: '',
  download_url: '',
  downloads: '',
  version: '1.0.0',
  image: '',
};

const formatNumber = (value) => Number(value || 0).toLocaleString();

const mapAppToForm = (app) => ({
  title: app?.title || '',
  description: app?.description || '',
  download_url: app?.download_url || '',
  downloads: app?.downloads ?? '',
  version: app?.version || '1.0.0',
  image: app?.image || app?.icon_url || '',
});

const buildPayload = (formData) => {
  const image = formData.image || Logo;

  return {
    title: formData.title.trim(),
    description: formData.description.trim(),
    download_url: formData.download_url.trim(),
    downloads: Number(formData.downloads) || 0,
    image,
    icon_url: image,
    cover_image: image,
    screenshots: [],
    developer: 'Quivix',
    version: formData.version.trim() || '1.0.0',
  };
};

const AdminAppsPage = () => {
  const [apps, setApps] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchApps();
  }, []);

  const fetchApps = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const res = await axios.get(`${apiUrl}/api/apps`);
      setApps(Array.isArray(res.data) ? res.data : res.data?.data || []);
    } catch (err) {
      toast.error('Failed to fetch apps catalog');
    }
  };

  const resetModal = () => {
    setEditingApp(null);
    setFormData(emptyForm);
    setShowModal(false);
    setIsSubmitting(false);
  };

  const openCreate = () => {
    setEditingApp(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEdit = (app) => {
    setEditingApp(app);
    setFormData(mapAppToForm(app));
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const apiUrl = process.env.REACT_APP_API_URL;
    const payload = buildPayload(formData);

    if (!payload.title) {
      toast.error('App Title is required');
      return;
    }

    setIsSubmitting(true);

    try {
      if (editingApp) {
        await axios.put(`${apiUrl}/api/apps/${editingApp.id}`, payload);
        toast.success('App updated successfully!');
      } else {
        await axios.post(`${apiUrl}/api/apps`, payload);
        toast.success('App created successfully!');
      }

      resetModal();
      fetchApps();
    } catch (err) {
      toast.error('Failed to save application');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this app?')) return;

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      await axios.delete(`${apiUrl}/api/apps/${id}`);
      toast.success('App deleted successfully!');
      fetchApps();
    } catch (err) {
      toast.error('Failed to delete application');
    }
  };

  const filteredApps = useMemo(() => {
    const needle = searchQuery.toLowerCase().trim();
    if (!needle) return apps;

    return apps.filter((app) =>
      [app?.title, app?.description, app?.developer]
        .join(' ')
        .toLowerCase()
        .includes(needle)
    );
  }, [apps, searchQuery]);

  const totalDownloads = useMemo(
    () => apps.reduce((sum, app) => sum + (Number(app?.downloads) || 0), 0),
    [apps]
  );

  return (
    <div className="space-y-8 animate-fade-in pb-12 font-sans">
      
      {/* Header Banner */}
      <section className="admin-hero-panel flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-xs font-mono mb-2">
            <Smartphone className="w-3.5 h-3.5" />
            <span>Applications Catalog</span>
          </div>
          <h1 className="admin-heading text-3xl sm:text-4xl font-black text-white tracking-tight">
            Ecosystem Applications
          </h1>
          <p className="admin-section-copy mt-1 text-slate-400 text-sm">
            Manage your live public apps: Title, Description, App Icon, Download Link, Version, and Download count.
          </p>
        </div>

        <div className="flex items-center gap-4">
          <div className="admin-mini-stat bg-white/5 border border-white/10 px-4 py-2 rounded-xl text-center">
            <span className="text-xl font-bold text-white font-mono">{apps.length}</span>
            <small className="block text-[10px] text-slate-400 font-mono uppercase">Apps</small>
          </div>
          <div className="admin-mini-stat bg-white/5 border border-white/10 px-4 py-2 rounded-xl text-center">
            <span className="text-xl font-bold text-emerald-400 font-mono">{formatNumber(totalDownloads)}</span>
            <small className="block text-[10px] text-slate-400 font-mono uppercase">Downloads</small>
          </div>
          <button onClick={openCreate} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs shadow-lg shadow-indigo-600/30 transition-all active:scale-95">
            <Plus size={18} />
            <span>Add New App</span>
          </button>
        </div>
      </section>

      {/* Toolbar & Search */}
      <div className="flex items-center justify-between gap-4 bg-black/40 border border-white/10 p-3 rounded-2xl">
        <div className="flex items-center gap-3 flex-1">
          <Search size={18} className="text-slate-400 ml-2" />
          <input
            type="text"
            placeholder="Search applications by title or description..."
            className="w-full bg-transparent border-none text-sm text-white placeholder-slate-500 focus:outline-none"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Apps Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredApps.map((app, index) => (
          <motion.article
            key={app.id || index}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.04 }}
            className="glass-card p-6 flex flex-col justify-between relative overflow-hidden group hover:border-indigo-500/30 transition-all"
          >
            <div>
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="w-14 h-14 rounded-2xl bg-neutral-900 border border-white/15 overflow-hidden flex items-center justify-center shrink-0 shadow-lg group-hover:scale-105 transition-transform">
                  <img src={app.image || app.icon_url || Logo} alt={app.title} className="w-full h-full object-cover" />
                </div>

                <div className="flex items-center gap-1.5">
                  <button onClick={() => openEdit(app)} className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white flex items-center justify-center transition-colors">
                    <Edit2 size={14} />
                  </button>
                  <button onClick={() => handleDelete(app.id)} className="w-8 h-8 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 flex items-center justify-center transition-colors">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-mono text-indigo-300 bg-indigo-500/10 border border-indigo-500/20 px-2 py-0.5 rounded-full">
                  v{app.version || '1.0.0'}
                </span>
                <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-full">
                  {formatNumber(app.downloads)} Downloads
                </span>
              </div>

              <h3 className="admin-heading text-lg font-bold text-white font-heading mt-2 group-hover:text-indigo-400 transition-colors">
                {app.title}
              </h3>
              <p className="text-xs text-slate-400 mt-2 line-clamp-3 leading-relaxed">
                {app.description}
              </p>
            </div>

            <div className="mt-6 pt-4 border-t border-white/5 flex items-center justify-between text-xs text-slate-400">
              {app.download_url ? (
                <a
                  href={app.download_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-indigo-400 hover:text-indigo-300 font-medium text-xs"
                >
                  <ExternalLink size={14} /> Download Link
                </a>
              ) : (
                <span className="text-slate-500 italic">No link specified</span>
              )}
            </div>
          </motion.article>
        ))}
      </div>

      {!filteredApps.length && (
        <div className="glass-card p-12 text-center text-slate-400 font-mono text-sm">
          No apps registered in database.
        </div>
      )}

      {/* Add / Edit App Modal */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={resetModal}
              className="fixed inset-0 bg-black/80 backdrop-blur-md"
            />

            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.96 }}
              className="glass-panel relative z-10 w-full max-w-2xl overflow-hidden border-white/10 my-auto rounded-3xl"
            >
              <div className="p-6 border-b border-white/10 flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-mono text-indigo-400 uppercase tracking-widest">
                    {editingApp ? 'Update Application' : 'Create Application'}
                  </span>
                  <h2 className="admin-heading text-2xl font-bold text-white font-heading mt-0.5">
                    {editingApp ? 'Edit App Details' : 'Add New Application'}
                  </h2>
                </div>
                <button onClick={resetModal} className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-slate-400 hover:text-white">
                  <X size={18} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-5 max-h-[80vh] overflow-y-auto">
                <div>
                  <label className="admin-form-label mb-1.5 block">App Title</label>
                  <input
                    type="text"
                    className="input-premium w-full"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g. Quivix Messenger"
                    required
                  />
                </div>

                <div>
                  <label className="admin-form-label mb-1.5 block">Description</label>
                  <textarea
                    className="input-premium w-full min-h-28 resize-none"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe app capabilities..."
                    required
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="admin-form-label mb-1.5 block">Download URL</label>
                    <input
                      type="url"
                      className="input-premium w-full"
                      value={formData.download_url}
                      onChange={(e) => setFormData({ ...formData, download_url: e.target.value })}
                      placeholder="https://play.google.com/..."
                    />
                  </div>

                  <div>
                    <label className="admin-form-label mb-1.5 block">Downloads Count</label>
                    <input
                      type="number"
                      min="0"
                      className="input-premium w-full"
                      value={formData.downloads}
                      onChange={(e) => setFormData({ ...formData, downloads: e.target.value })}
                      placeholder="60000"
                    />
                  </div>
                </div>

                <div>
                  <label className="admin-form-label mb-1.5 block">Version Tag</label>
                  <input
                    type="text"
                    className="input-premium w-full"
                    value={formData.version}
                    onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                    placeholder="1.0.0"
                  />
                </div>

                <div>
                  <ImageDropzone
                    label="App Icon Image"
                    value={formData.image}
                    onChange={(image) => setFormData({ ...formData, image })}
                  />
                </div>

                <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/10">
                  <button type="button" onClick={resetModal} className="px-5 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-white font-medium text-xs">
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs shadow-lg shadow-indigo-600/30 transition-all active:scale-95"
                  >
                    <span>{isSubmitting ? 'Saving...' : editingApp ? 'Save Changes' : 'Create Application'}</span>
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminAppsPage;
